import { useState, useEffect, useRef } from 'react'
import { IAccountFilters, IFilterConfig } from '../types'
import { constructNewFilterFromConfig, constructUserFiltersFromUrl } from '../components/filters'
import * as lib from '../lib'
import { EFilterOperator, EFilterType, EFilterSpecOperator, EFilterFetchConvertType } from '../types/enum'
import { ROLE_EXTRA_IS_BUYER_OPTIONS, ACCOUNT_IS_FIRED_OPTIONS, ACCOUNT_IS_ACTIVE_OPTIONS } from '../constants'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ALL_LANG_KEY } from '../types/lang'
import { useAtomValue } from 'jotai'

type IUseFiltersAccountPage = {
	filter: [string, any][];
	send: boolean;
	activeFiltersCount: number;
	filterFillFactor: number;
	isFiltersPanelOpen: boolean;
	setIsFiltersPanelOpen: (value: (((prevState: boolean) => boolean) | boolean)) => void;
	accountFiltersConfig: IFilterConfig<keyof IAccountFilters>[];
	defaultFiltersValue: IAccountFilters;
	initialFiltersValue: IAccountFilters;
	clearChildFilters: number;
	setUserFilters: (value: (((prevState: (IAccountFilters | null)) => (IAccountFilters | null)) | IAccountFilters | null)) => void;
}

const ACCOUNT_FILTERS_DEFAULT: IAccountFilters = {
	email: '',
	'auth.login': '',
	'role.name': '',
	roleId: null,
	isActive: null,
	isFired: null,
	registeredAt: '',
	'extra.keitaroLogin': '',
	'role.extra.inGroup': '',
	'role.extra.isBuyer': null
}


export const useFiltersAccountPage = (): IUseFiltersAccountPage => {
	const { t } = useTranslation()
	const isFirstRender = useRef<boolean>(true)

	// const profile = useAtomValue(state.profileAtom)

	const [searchParams, setSearchParams] = useSearchParams()

	const urlFilter = (() => {
		try {
			return lib.filterSearchParams(searchParams.get('f')) ?? []
		} catch (error) {
			console.error(error)
			return []
		}
	})()

	const [filter, setFilter] = useState<[string, any][]>([...urlFilter]) // main filter with mixins
	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState<boolean>(false)
	const [activeFiltersCount, setActiveFiltersCount] = useState<number>(0)

	const [clearChildFilters, setClearChildFilters] = useState<number>(0)
	const [send, setSend] = useState<boolean>(false)
	const [userFilters, setUserFilters] = useState<IAccountFilters | null>(null)

	const filterFillFactor = !!userFilters ? (100 * activeFiltersCount / Object.keys(userFilters).length) : 0

	const ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS = ROLE_EXTRA_IS_BUYER_OPTIONS(t)
	const ACCOUNT_IS_ACTIVE_OPTIONS_TRANS = ACCOUNT_IS_ACTIVE_OPTIONS(t)
	const ACCOUNT_IS_FIRED_OPTIONS_TRANS = ACCOUNT_IS_FIRED_OPTIONS(t)

	const accountFiltersConfig: IFilterConfig<keyof IAccountFilters>[] = [
		// {
		// 	name: 'code',
		// 	label: 'Code',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
		{
			name: 'email',
			label: t(ALL_LANG_KEY.account_list_table.column_email),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'auth.login',
			label: t(ALL_LANG_KEY.account_list_table.column_login),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'extra.keitaroLogin',
			label: t(ALL_LANG_KEY.account_list_table.column_kt_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.name',
			label: t(ALL_LANG_KEY.account_list_table.column_role),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'role.extra.isBuyer',
			label: t(ALL_LANG_KEY.account_list_table.column_is_buyer),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ROLE_EXTRA_IS_BUYER_OPTIONS_TRANS,
			styles: { minWidth: '200px' }
		},
		{
			name: 'role.extra.inGroup',
			label: t(ALL_LANG_KEY.account_list_table.column_in_group),
			type: EFilterType.input,
			operator: EFilterOperator.isLike,
			specOperator: EFilterSpecOperator.everywhere,
			styles: { width: '200px' }
		},
		{
			name: 'isActive',
			label: t(ALL_LANG_KEY.account_list_table.column_is_active),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ACCOUNT_IS_ACTIVE_OPTIONS_TRANS,
			styles: { minWidth: '160px' }
		},
		{
			name: 'isFired',
			label: t(ALL_LANG_KEY.account_list_table.column_is_fired),
			type: EFilterType.selectSingle,
			fetchConvertType: EFilterFetchConvertType.boolean,
			operator: EFilterOperator.isEqual,
			options: ACCOUNT_IS_FIRED_OPTIONS_TRANS,
			styles: { minWidth: '160px' }
		}
		// {
		// 	name: 'registeredAt',
		// 	label: 'Registered At',
		// 	type: EFilterType.input,
		// 	operator: EFilterOperator.isLike,
		// 	specOperator: EFilterSpecOperator.everywhere,
		// 	styles: { width: '200px' }
		// },
	]

	const initialUserFilters = constructUserFiltersFromUrl(
		urlFilter,
		accountFiltersConfig,
		ACCOUNT_FILTERS_DEFAULT
	)

	useEffect(() => {
		//console.log('hook.useFiltersAccountPage useEffect::START:: ', { userFilters, filter })
		if (!!userFilters) {
			setSend(true)

			const { newFilter, activeFiltersCnt } = constructNewFilterFromConfig(
				userFilters,
				accountFiltersConfig
			)
			const newFilterStr = lib.filterSetup([...newFilter ?? []])

			setSearchParams((prev) => {
				if (newFilterStr) {
					prev.set('f', newFilterStr)
				} else {
					prev.delete('f')
				}

				// after first pageLoad/pageRefresh resetting is not needed
				if (!isFirstRender.current) {
					prev.delete('page')
				} else {
					isFirstRender.current = false
				}

				return prev
			})

			setFilter(newFilter)
			setActiveFiltersCount(activeFiltersCnt)

			// to open FiltersPanel if there were previously active filters on the page [onRefreshPage case]
			if (activeFiltersCnt > 0) {
				setIsFiltersPanelOpen(true)
			}
			console.log('hook.useFiltersAccountPage:: useEffect::END:: ', { userFilters, filter, newFilter, newFilterStr })
		}
	}, [userFilters])

	return {
		filter,
		send,
		activeFiltersCount,
		filterFillFactor,
		isFiltersPanelOpen,
		setIsFiltersPanelOpen,
		accountFiltersConfig,
		defaultFiltersValue: ACCOUNT_FILTERS_DEFAULT,
		initialFiltersValue: initialUserFilters,
		clearChildFilters,
		setUserFilters
	}
}